import { postRequest } from "../Fetch/request";

/**
 * Retry configuration for AI API calls
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [500, 502, 503, 504, 408, 429],
  retryableErrors: [
    'parsing',
    'timeout',
    'network',
    'connection',
    'json',
    'syntax',
    'unexpected token',
    'failed to parse',
    'schema',
    'validation',
    'does not match expected schema',
    'missing required field',
    'type mismatch',
    'invalid field'
  ]
};

/**
 * Validate response against JSON schema
 * @param {any} response - The response to validate
 * @param {Object} schema - The expected schema
 * @returns {boolean} - Whether the response matches the schema
 */
const validateResponseSchema = (response, schema) => {
  if (!schema) {
    return true; // Skip validation if no schema provided
  }
  
  if (!response) {
    return false;
  }
  
  try {
    // Handle string responses that might be JSON
    let parsedResponse = response;
    if (typeof response === 'string') {
      try {
        parsedResponse = JSON.parse(response);
      } catch (e) {
        return false;
      }
    }
    
    // Recursive schema validation
    const validateObject = (obj, schemaObj, path = '') => {
      // Handle string type (primitive type check)
      if (typeof schemaObj === 'string') {
        const isValid = typeof obj === schemaObj;
        return isValid;
      }
      
      // Handle array schemas (e.g., mistakes: [{...}])
      if (Array.isArray(schemaObj)) {
        // Schema is an array, so response should also be an array
        if (!Array.isArray(obj)) {
          return false;
        }
        
        // If array is empty, that's valid
        if (obj.length === 0) {
          return true;
        }
        
        // Validate each item in the array against the schema item (first element of schema array)
        const itemSchema = schemaObj[0];
        return obj.every((item, index) => {
          const itemPath = path ? `${path}[${index}]` : `[${index}]`;
          return validateObject(item, itemSchema, itemPath);
        });
      }
      
      // Handle object schemas
      if (typeof schemaObj === 'object' && schemaObj !== null) {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
          return false;
        }
        
        // Check all required fields (fields that are NOT optional)
        // Optional fields are marked with a comment or can be inferred
        // For now, we'll check all fields in schema, but make it more lenient
        for (const [key, expectedType] of Object.entries(schemaObj)) {
          const currentPath = path ? `${path}.${key}` : key;
          
          // If field exists, validate it
          if (key in obj) {
            if (!validateObject(obj[key], expectedType, currentPath)) {
              return false;
            }
          }
          // If field doesn't exist, we'll allow it for now (treating all as optional for flexibility)
          // In a stricter validation, we could mark required fields differently
        }
        
        // Ensure at least the most critical fields are present
        // For mistakes: severity, description, suggestion are critical
        // For strengths: description is critical
        // For root: overall_assessment, mistakes, strengths, summary are critical
        
        return true;
      }
      
      return true;
    };
    
    const isValid = validateObject(parsedResponse, schema);
    
    return isValid;
    
  } catch (error) {
    return false;
  }
};

/**
 * Check if an error is retryable based on status code or error message
 * @param {Error|Object} error - The error object
 * @param {number} statusCode - HTTP status code (if available)
 * @returns {boolean} - Whether the error is retryable
 */
const isRetryableError = (error, statusCode = null) => {
  // Check status code
  if (statusCode && RETRY_CONFIG.retryableStatusCodes.includes(statusCode)) {
    return true;
  }

  // Check error message for retryable patterns
  const errorMessage = (error?.message || error?.toString() || '').toLowerCase();
  const isRetryable = RETRY_CONFIG.retryableErrors.some(pattern => 
    errorMessage.includes(pattern)
  );
  
  // Also retry on schema validation failures
  if (errorMessage.includes('schema') || errorMessage.includes('validation')) {
    return true;
  }
  
  return isRetryable;
};

/**
 * Calculate delay for exponential backoff with jitter
 * @param {number} attempt - Current attempt number (0-based)
 * @returns {number} - Delay in milliseconds
 */
const calculateDelay = (attempt) => {
  const exponentialDelay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, RETRY_CONFIG.maxDelay);
  
  // Add jitter (±25% randomization)
  const jitter = cappedDelay * 0.25 * (Math.random() - 0.5);
  return Math.max(cappedDelay + jitter, RETRY_CONFIG.baseDelay);
};

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} - Promise that resolves after delay
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry wrapper for AI API calls with exponential backoff
 * @param {Function} apiCall - The API call function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {Function} options.onRetry - Callback called before each retry (attempt, error, delay)
 * @param {Function} options.shouldRetry - Custom function to determine if error is retryable
 * @returns {Promise} - Promise that resolves with API response or rejects with final error
 */
export const withRetry = async (apiCall, options = {}) => {
  const {
    maxRetries = RETRY_CONFIG.maxRetries,
    onRetry = null,
    shouldRetry = isRetryableError
  } = options;

  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      
      const result = await apiCall();
      
      // Success - return result
      return result;
      
    } catch (error) {
      lastError = error;
      
      // If this is the last attempt, don't retry
      if (attempt === maxRetries) {
        break;
      }
      
      // Check if error is retryable
      const statusCode = error.status || error.statusCode || null;
      if (!shouldRetry(error, statusCode)) {
        break;
      }
      
      // Calculate delay for next attempt
      const delay = calculateDelay(attempt);
      
      // Call onRetry callback if provided
      if (onRetry) {
        try {
          onRetry(attempt + 1, error, delay);
        } catch (callbackError) {
        }
      }
      
      // Wait before retrying
      await sleep(delay);
    }
  }
  
  // All retries exhausted, throw the last error
  throw lastError;
};

/**
 * Retry wrapper specifically for AI sync calls
 * @param {string} endpoint - API endpoint
 * @param {Object} payload - Request payload
 * @param {Object} options - Retry options
 * @param {Object} options.schema - JSON schema for response validation
 * @returns {Promise} - Promise that resolves with API response
 */
export const retryAISync = async (endpoint, payload, options = {}) => {
  const { schema, ...retryOptions } = options;
  
  return withRetry(
    async () => {
      const response = await postRequest(endpoint, payload);
      
      // Validate response structure
      if (!response || !response.success || !response.data || response.data.response === undefined) {
        throw new Error('Invalid AI response structure');
      }
      
      // Additional validation for empty responses
      let responseContent = response.data.response;
      
      // Strip markdown code blocks from string responses BEFORE validation
      if (typeof responseContent === 'string') {
        if (!responseContent.trim()) {
          throw new Error('Empty AI response received');
        }
        
        // Remove markdown code blocks if present (```json ... ``` or ``` ... ```)
        let cleanedContent = responseContent.trim();
        if (cleanedContent.startsWith('```')) {
          // Remove opening ```json or ```
          cleanedContent = cleanedContent.replace(/^```(?:json)?\s*\n?/i, '');
          // Remove closing ```
          cleanedContent = cleanedContent.replace(/\n?```\s*$/i, '');
          cleanedContent = cleanedContent.trim();
          
          // Update response with cleaned content
          response.data.response = cleanedContent;
          responseContent = cleanedContent;
        }
      } else if (typeof responseContent === 'object' && responseContent !== null) {
        // Handle object responses (might be JSON)
      } else if (!responseContent) {
        throw new Error('Invalid AI response content type');
      }
      
      // Schema validation if schema is provided (now using cleaned content)
      if (schema) {
        const isValidSchema = validateResponseSchema(responseContent, schema);
        if (!isValidSchema) {
          // Only retry if response was not successful or if it's a parsing error
          // Don't retry on schema validation failures if API returned success - just log it
          if (response.success) {
            console.warn('Schema validation failed but API returned success. Response:', responseContent);
            // Return the response anyway - we'll handle parsing in the component
            return response;
          }
          // Create detailed error message for better retry handling
          const errorMsg = `AI response does not match expected schema format. Expected structured response with all required fields, but got invalid or incomplete data. This will trigger a retry.`;
          throw new Error(errorMsg);
        }
      }
      
      return response;
    },
    {
      ...retryOptions,
      onRetry: (attempt, error, delay) => {
        // Call user-provided onRetry callback if exists
        if (retryOptions.onRetry) {
          retryOptions.onRetry(attempt, error, delay);
        }
      }
    }
  );
};

/**
 * Enhanced error handler for AI responses with better error messages
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 * @returns {Error} - Enhanced error with better message
 */
export const enhanceAIError = (error, context = 'AI API call') => {
  const originalMessage = error.message || 'Unknown error';
  
  // Map common errors to user-friendly messages
  const errorMappings = {
    'parsing': 'AI response parsing failed - the service may be experiencing issues',
    'timeout': 'AI service timeout - please try again',
    'network': 'Network connection issue - check your internet connection',
    'json': 'Invalid response format from AI service',
    'syntax': 'AI response format error - service may be temporarily unavailable',
    '500': 'AI service internal error - retrying automatically',
    '502': 'AI service gateway error - retrying automatically',
    '503': 'AI service temporarily unavailable - retrying automatically',
    '504': 'AI service timeout - retrying automatically'
  };
  
  // Find matching error pattern
  const matchedPattern = Object.keys(errorMappings).find(pattern =>
    originalMessage.toLowerCase().includes(pattern)
  );
  
  if (matchedPattern) {
    const enhancedMessage = `${context}: ${errorMappings[matchedPattern]}`;
    const enhancedError = new Error(enhancedMessage);
    enhancedError.originalError = error;
    enhancedError.context = context;
    return enhancedError;
  }
  
  // Return original error with context if no mapping found
  const enhancedError = new Error(`${context}: ${originalMessage}`);
  enhancedError.originalError = error;
  enhancedError.context = context;
  return enhancedError;
};

/**
 * Get retry statistics for monitoring
 * @returns {Object} - Retry configuration and statistics
 */
export const getRetryConfig = () => ({
  ...RETRY_CONFIG,
  version: '1.0.0',
  description: 'AI API retry mechanism with exponential backoff'
});

/**
 * Test function to demonstrate schema validation
 * @param {Object} testResponse - Response to test
 * @param {Object} schema - Schema to validate against
 * @returns {boolean} - Validation result
 */
const testSchemaValidation = (testResponse, schema) => {
  const result = validateResponseSchema(testResponse, schema);
  return result;
};

export { validateResponseSchema, testSchemaValidation };

export default {
  withRetry,
  retryAISync,
  enhanceAIError,
  getRetryConfig,
  isRetryableError,
  calculateDelay,
  validateResponseSchema,
  testSchemaValidation
};
